import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CountdownTimer } from './CountdownTimer';
import { SparklesIcon, CheckCircle2Icon, GiftIcon, PackageIcon } from 'lucide-react';
const BASE_PRICE_USD = 13.99;
const OLD_PRICE_USD = 27.99;
const MARKUP = 1.05;
interface CurrencyInfo {
  code: string;
  symbol: string;
  rate: number;
  locale: string;
}
const currencyMap: Record<string, CurrencyInfo> = {
  AR: {
    code: 'ARS',
    symbol: '$',
    rate: 900,
    locale: 'es-AR'
  },
  MX: {
    code: 'MXN',
    symbol: '$',
    rate: 17.5,
    locale: 'es-MX'
  },
  CO: {
    code: 'COP',
    symbol: '$',
    rate: 4200,
    locale: 'es-CO'
  },
  CL: {
    code: 'CLP',
    symbol: '$',
    rate: 950,
    locale: 'es-CL'
  },
  PE: {
    code: 'PEN',
    symbol: 'S/',
    rate: 3.75,
    locale: 'es-PE'
  },
  UY: {
    code: 'UYU',
    symbol: '$',
    rate: 40,
    locale: 'es-UY'
  },
  BR: {
    code: 'BRL',
    symbol: 'R$',
    rate: 5.1,
    locale: 'pt-BR'
  },
  ES: {
    code: 'EUR',
    symbol: '€',
    rate: 0.92,
    locale: 'es-ES'
  },
  US: {
    code: 'USD',
    symbol: '$',
    rate: 1,
    locale: 'en-US'
  }
};
function detectCurrency(): CurrencyInfo {
  const lang = navigator.language || 'es';
  const parts = lang.split('-');
  const country = parts.length > 1 ? parts[1].toUpperCase() : '';
  if (country && currencyMap[country]) {
    return currencyMap[country];
  }
  const langLower = lang.toLowerCase();
  if (langLower.startsWith('es')) {
    const tzOffset = new Date().getTimezoneOffset();
    if (tzOffset >= 180 && tzOffset <= 240) return currencyMap['AR'];
    if (tzOffset >= 300 && tzOffset <= 360) return currencyMap['MX'];
    if (tzOffset >= 240 && tzOffset < 300) return currencyMap['CO'];
    return currencyMap['ES'];
  }
  if (langLower.startsWith('pt')) return currencyMap['BR'];
  return currencyMap['US'];
}
function formatPrice(amount: number, currency: CurrencyInfo): string {
  try {
    return new Intl.NumberFormat(currency.locale, {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: currency.code === 'CLP' || currency.code === 'COP' || currency.code === 'ARS' ? 0 : 2,
      maximumFractionDigits: currency.code === 'CLP' || currency.code === 'COP' || currency.code === 'ARS' ? 0 : 2
    }).format(amount);
  } catch {
    return `${currency.symbol}${amount.toFixed(2)}`;
  }
}
const mainModules = [{
  name: 'Módulo de Técnica',
  desc: 'Ejercicios para mejorar golpes y fundamentos',
  priceUsd: 9.99
}, {
  name: 'Módulo de Táctica',
  desc: 'Situaciones de juego para tomar mejores decisiones',
  priceUsd: 9.99
}, {
  name: 'Módulo de Bandejas',
  desc: 'Técnicas y ejercicios para bandejas ganadoras',
  priceUsd: 7.99
}, {
  name: 'Módulo de Salidas de Pared',
  desc: 'Patrones y combinaciones para construir el punto',
  priceUsd: 7.99
}, {
  name: 'Módulo de Juego Aéreo',
  desc: 'Mejora tu smash y bandeja por 3 para ser letal',
  priceUsd: 7.99
}, {
  name: 'Módulo de Defensa',
  desc: 'Ejercicios para defender con seguridad y contraatacar',
  priceUsd: 7.99
}, {
  name: 'Módulo de Transiciones',
  desc: 'Cómo pasar de defensa a ataque de forma efectiva',
  priceUsd: 7.99
}, {
  name: 'Módulo de Estrategia',
  desc: 'Planes de juego para distintos rivales y escenarios',
  priceUsd: 9.99
}, {
  name: 'Módulo de Saque',
  desc: 'Rutinas para un saque más potente y efectivo',
  priceUsd: 7.99
}, {
  name: 'Módulo de Voleas',
  desc: 'Ejercicios para dominar la red y definir puntos',
  priceUsd: 7.99
}, {
  name: 'Sesiones Completas',
  desc: 'Clases listas para aplicar con objetivos claros',
  priceUsd: 12.99
}, {
  name: 'Análisis de Rival',
  desc: 'Claves para estudiar y ganar a cualquier oponente',
  priceUsd: 9.99
}];
const bonuses = [{
  name: 'BONO: Módulo de Físico',
  desc: 'Rutinas para mejorar resistencia, velocidad y explosividad',
  priceUsd: 9.99
}, {
  name: 'BONO: Juegos Reducidos',
  desc: 'Formatos de juegos para entrenar situaciones reales',
  priceUsd: 7.99
}, {
  name: 'BONO: Recursos Prácticos para Entrenadores',
  desc: 'Material complementario listo para usar en tus clases',
  priceUsd: 12.99
}];
export function OfferSection() {
  const checkoutUrl = 'https://pay.hotmart.com/A105284571O?checkoutMode=10';
  const [currency, setCurrency] = useState<CurrencyInfo>(currencyMap['US']);
  useEffect(() => {
    setCurrency(detectCurrency());
  }, []);
  const localPrice = Math.round(BASE_PRICE_USD * currency.rate * MARKUP * 100) / 100;
  const localOldPrice = Math.round(OLD_PRICE_USD * currency.rate * MARKUP * 100) / 100;
  const totalModulesValue = mainModules.reduce((sum, m) => sum + m.priceUsd, 0);
  const totalBonusValue = bonuses.reduce((sum, b) => sum + b.priceUsd, 0);
  const totalValue = totalModulesValue + totalBonusValue;
  const localTotalValue = Math.round(totalValue * currency.rate * MARKUP * 100) / 100;
  return <section id="compra" className="py-16 px-4 bg-gradient-to-b from-card/50 to-dark" data-id="element-71">
      <div className="max-w-4xl mx-auto" data-id="element-72">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-73">
          <h2 className="text-3xl md:text-4xl font-black mb-6" data-id="element-74">
            Consigue el{' '}
            <span className="text-accent" data-id="element-75">Megapack Pádel Listo</span>
          </h2>

          <img src="https://cdn.magicpatterns.com/uploads/ouZTqykmJ4RJBR9uYpPXbW/Megapack_para_entrenadores_de_padel.png" alt="Megapack Pádel Listo" className="w-full max-w-2xl mx-auto mb-10 drop-shadow-[0_0_30px_rgba(132,204,22,0.15)]" style={{
          mixBlendMode: 'lighten'
        }} data-id="element-76" />

          {/* WHAT'S INCLUDED */}
          <div className="text-left max-w-2xl mx-auto mb-10" data-id="element-77">
            <div className="flex items-center gap-3 mb-6 justify-center" data-id="element-78">
              <PackageIcon className="w-7 h-7 text-accent" data-id="element-79" />
              <h3 className="text-2xl md:text-3xl font-black text-center" data-id="element-80">
                Todo lo que incluye el pack:
              </h3>
            </div>

            <div className="space-y-3 mb-8" data-id="element-81">
              {mainModules.map((mod, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="flex items-start gap-3 bg-card/60 border border-gray-700/50 rounded-lg p-3" data-id="element-82">
                  <CheckCircle2Icon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" data-id="element-83" />
                  <div className="flex-1" data-id="element-84">
                    <div className="flex items-center justify-between gap-2 flex-wrap" data-id="element-85">
                      <p className="font-bold text-sm md:text-base" data-id="element-86">
                        {mod.name}
                      </p>
                      <p className="text-gray-400 line-through text-sm" data-id="element-87">
                        {formatPrice(Math.round(mod.priceUsd * currency.rate * MARKUP * 100) / 100, currency)}
                      </p>
                    </div>
                    <p className="text-gray-400 text-xs md:text-sm" data-id="element-88">
                      {mod.desc}
                    </p>
                  </div>
                </motion.div>)}
            </div>

            {/* BONUSES */}
            <div className="flex items-center gap-3 mb-6 justify-center" data-id="element-89">
              <GiftIcon className="w-7 h-7 text-accent" data-id="element-90" />
              <h3 className="text-2xl md:text-3xl font-black text-center" data-id="element-91">
                Bonos <span className="text-accent" data-id="element-92">GRATIS</span> por hoy:
              </h3>
            </div>

            <div className="space-y-3 mb-8" data-id="element-93">
              {bonuses.map((bonus, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="flex items-start gap-3 bg-accent/5 border-2 border-accent/30 rounded-lg p-3 relative overflow-hidden" data-id="element-94">
                  <div className="absolute top-0 right-0 bg-accent text-dark text-xs font-black px-3 py-1 rounded-bl-lg" data-id="element-95">
                    GRATIS
                  </div>
                  <GiftIcon className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" data-id="element-96" />
                  <div className="flex-1" data-id="element-97">
                    <div className="flex items-center justify-between gap-2 flex-wrap" data-id="element-98">
                      <p className="font-bold text-sm md:text-base text-accent" data-id="element-99">
                        {bonus.name}
                      </p>
                      <p className="text-gray-400 line-through text-sm" data-id="element-100">
                        {formatPrice(Math.round(bonus.priceUsd * currency.rate * MARKUP * 100) / 100, currency)}
                      </p>
                    </div>
                    <p className="text-gray-400 text-xs md:text-sm" data-id="element-101">
                      {bonus.desc}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>

          {/* VALUE STACK */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="bg-card border-2 border-primary/30 rounded-2xl p-6 md:p-8 mb-10 max-w-2xl mx-auto" data-id="element-102">
            <p className="text-lg text-gray-300 mb-2" data-id="element-103">Valor total del pack:</p>
            <p className="text-3xl md:text-4xl font-black text-gray-400 line-through mb-4" data-id="element-104">
              {formatPrice(localTotalValue, currency)}
            </p>

            <div className="w-16 h-1 bg-accent mx-auto mb-4 rounded-full" data-id="element-105" />

            <p className="text-lg text-gray-300 mb-2" data-id="element-106">
              Hoy lo llevas todo por:
            </p>

            <div className="bg-red-600/20 border border-red-500 rounded-lg p-3 mb-4 inline-block" data-id="element-107">
              <p className="text-xs mb-1" data-id="element-108">Oferta válida por:</p>
              <CountdownTimer className="text-xl text-red-400" data-id="element-109" />
            </div>

            <div className="mb-6" data-id="element-110">
              <p className="text-gray-400 line-through text-2xl mb-1" data-id="element-111">
                {formatPrice(localOldPrice, currency)}
              </p>
              <p className="text-5xl md:text-6xl font-black text-primary mb-1" data-id="element-112">
                {formatPrice(localPrice, currency)}
              </p>
              <p className="text-sm text-gray-400" data-id="element-113">
                Precio especial por tiempo limitado
              </p>
              {currency.code !== 'USD' && <p className="text-xs text-gray-500 mt-1" data-id="element-114">
                  (USD ${BASE_PRICE_USD} • precio aproximado en {currency.code})
                </p>}
            </div>

            <p className="text-accent font-bold text-lg mb-6" data-id="element-115">
              ¡Ahorras más del{' '}
              {Math.round((1 - BASE_PRICE_USD / totalValue) * 100)}%!
            </p>

            <motion.a href={checkoutUrl} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="inline-block bg-primary hover:bg-green-600 text-white font-bold text-xl px-16 py-6 rounded-lg shadow-2xl transition-all mb-4" data-id="element-116">
              🔥 Descubrir el sistema ahora
            </motion.a>

            <p className="text-sm text-gray-400" data-id="element-117">
              Te tomará menos de 2 minutos entender cómo funciona.
            </p>
          </motion.div>

          <div className="flex items-center justify-center gap-2 text-sm text-gray-400" data-id="element-118">
            <SparklesIcon className="w-4 h-4" data-id="element-119" />
            <p data-id="element-120">Acceso inmediato • Garantía 45 días • Pago 100% seguro</p>
            <SparklesIcon className="w-4 h-4" data-id="element-121" />
          </div>
        </motion.div>
      </div>
    </section>;
}